import { Box, Stack, useTheme } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { WfButton, WfIconNavigationCloseRegular, WfLegend } from "@WahooFitness/redesignr";
import WfActionGroup from "@WahooFitness/redesignr/dist/Actions/WfActionGroup/WfActionGroup.js";
import { FitnessProfileResponseType } from "@WahooFitness/cloud-client-types";
import FullScreenDialog from "@/components/FullScreenDialog";
import FormRow from "@/components/ProfileEdit/FormRow";
import useManualCyclingProfileUpdate from "./useManualCyclingProfileUpdate";
import { FitnessProfileEditForm } from "@/components/ProfileEdit/useFourDpEdit";
import PowerZoneUpdate from "@/components/Quiz/4dpQuiz/PowerZoneUpdate/PowerZoneUpdate";
import { SourcePage } from "@/services/Analytics/EventTypes";

const ManualCyclingProfileUpdateDialog = ({
  open,
  handleClose,
  cyclingFitnessProfilesData,
}: {
  open: boolean;
  handleClose: () => void;
  cyclingFitnessProfilesData: FitnessProfileResponseType;
}) => {
  const { breakpoints, palette } = useTheme();

  const legendItems = [
    {
      id: "ftp",
      name: "FTP",
      primaryText: t`Threshold Power`,
      color: palette.zones["4dp"].ftp.main,
    },
    {
      id: "map",
      name: "MAP",
      primaryText: t`Maximal Aerobic Power`,
      color: palette.zones["4dp"].map.main,
    },
    {
      id: "ac",
      name: "AC",
      primaryText: t`Anaerobic Capacity`,
      color: palette.zones["4dp"].ac.main,
    },
    { id: "nm", name: "NM", primaryText: t`Neuro-muscular`, color: palette.zones["4dp"].nm.main },
  ];

  const {
    formData,
    formIsTouched,
    handleInputChange,
    handleBlur,
    handleFocus,
    handleReset,
    handleSave,
    handleCloseModal,
    handleCloseWithMessage,
    isLoading,
    isSaveButtonDisabled,
    showPowerZoneUpdate,
  } = useManualCyclingProfileUpdate({
    cyclingFitnessProfile: cyclingFitnessProfilesData,
    handleClose,
  });

  const powerFields: FitnessProfileEditForm[] = formData.slice(0, 4);
  const otherFields: FitnessProfileEditForm[] = formData.slice(4);

  return (
    <FullScreenDialog
      backgroundColor={palette.background.level1}
      slideDirection="up"
      edgeToEdge
      open={open}
      handleClose={handleCloseModal}
      headerProps={{
        backgroundColorOverride: palette.background.level1,
        title: t`Update manually`,
        primaryLeftActionItem: {
          icon: <WfIconNavigationCloseRegular />,
          action: showPowerZoneUpdate ? () => handleCloseWithMessage(false) : handleCloseModal,
        },
      }}
    >
      {showPowerZoneUpdate ? (
        <PowerZoneUpdate
          results={{
            profile: {
              fourdp: {
                FTP: formData[0].value ?? 0,
                MAP: formData[1].value ?? 0,
                AC: formData[2].value ?? 0,
                NM: formData[3].value ?? 0,
              },
            },
          }}
          sourcePage={SourcePage.fourdp_manual_update_powerzones}
          onClose={handleCloseWithMessage}
        />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          height="100%"
          bgcolor="background.level1"
        >
          <Box display="flex" flexDirection="column" px={2} py={1.5} rowGap={1.5}>
            <Stack spacing={1} width="100%" maxWidth="sm" mx="auto">
              {powerFields.map((item: FitnessProfileEditForm, index: number) => (
                <FormRow
                  key={item.key}
                  value={item.value}
                  label={item.subLabel ?? item.label}
                  subLabel={item.time}
                  handleInputChange={handleInputChange}
                  colorStart={item.itemStartColor}
                  colorEnd={item.itemEndColor}
                  endAdornment={item.endAdornment}
                  handleBlur={handleBlur}
                  index={index}
                  maxValue={item.maxValue}
                  hasError={item.hasError}
                  handleFocus={handleFocus}
                />
              ))}
            </Stack>

            <Box width="100%" maxWidth="sm" mx="auto">
              <WfLegend loadItems={legendItems} wrapItems />
            </Box>

            <Stack spacing={1} width="100%" maxWidth="sm" mx="auto">
              {otherFields.map((item: FitnessProfileEditForm, index: number) => (
                <FormRow
                  key={item.key}
                  value={item.value}
                  label={item.label}
                  handleInputChange={handleInputChange}
                  startAdornment={item.startAdornment}
                  endAdornment={item.endAdornment}
                  handleBlur={handleBlur}
                  index={index + powerFields.length}
                  maxValue={item.maxValue}
                  hasError={item.hasError}
                  handleFocus={handleFocus}
                  description={item.description}
                />
              ))}
            </Stack>
          </Box>
          <Box width="100%" mt="auto" maxWidth="sm" py={2} display="flex" alignSelf="center">
            <WfButton
              hierarchy="text"
              intent="destructive"
              size="large"
              fullWidth
              onClick={handleReset}
              disabled={!formIsTouched}
            >
              <Trans>Reset values</Trans>
            </WfButton>
          </Box>
          <WfActionGroup orientation="vertical" fullWidth sticky maxContentWidth="sm">
            <WfButton
              hierarchy="primary"
              intent="default"
              size="large"
              fullWidth
              onClick={handleSave}
              disabled={isSaveButtonDisabled}
              sx={{
                maxWidth: breakpoints.values.sm,
              }}
            >
              {isLoading ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
            </WfButton>
          </WfActionGroup>
        </Box>
      )}
    </FullScreenDialog>
  );
};

export default ManualCyclingProfileUpdateDialog;
